import {
  ChangeDetectionStrategy,
  Component,
  DestroyRef,
  computed,
  inject,
  input,
} from '@angular/core';
import {
  takeUntilDestroyed,
  toObservable,
  toSignal,
} from '@angular/core/rxjs-interop';

import { catchError, of, switchMap } from 'rxjs';

import { EMPTY_WARNINGS } from '@shared-constants';

import { CookieAuthService } from 'src/app/@shared/storage-variables/cookie-auth.service';

import { InventoryJobsService } from '../../../features/inventory-jobs/services/inventory-jobs.service';
import { InventoryWarningNotificationStoreService } from '../../services/inventory-warning-notification/inventory-warning-notification-store.service';

/**
 * Headless reporter for the inventory outbox (see `V1-Insight-Api`'s
 * `inventory_outbox` / `InventoryJobsService.getArticleWarnings`). Renders
 * nothing itself — visible presentation lives in
 * `InventoryWarningNotificationComponent`, a single globally-mounted widget.
 * This component's only job is: query this one article's warning status,
 * and register/unregister with `InventoryWarningNotificationStoreService`
 * so that widget's aggregated view stays accurate for whatever is currently
 * on screen.
 *
 * One instance covers exactly one article — the "list" case (many articles
 * on one screen) is the consuming screen's job: it renders one of these per
 * row, each with its own `articleId`/`articleCode`.
 */
@Component({
  selector: 'v-one-inventory-article-warning',
  standalone: true,
  template: '',
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class InventoryArticleWarningComponent {
  private readonly inventoryJobsService = inject(InventoryJobsService);
  private readonly cookieAuthService = inject(CookieAuthService);
  private readonly store = inject(InventoryWarningNotificationStoreService);

  /**
   * Accepts `string` as well as `number` on purpose. The legacy `Article`
   * model types `id_articulo` as `number`, but the legacy call sites reach it
   * through `form.get('article')?.value`, which is `any` — so a string id
   * flows in with no compile-time error and is only saved by the `Number(...)`
   * coercion below. Declaring the input as `number` alone made that contract
   * a fiction: it promised something the template bindings never guaranteed.
   * Widening it states what callers actually pass, and `numericArticleId`
   * does the narrowing in one place.
   */
  readonly articleId = input.required<number | string>();
  /** The backend has no product-code field on this type — every caller
   * already holds the full article object and passes its code through. */
  readonly articleCode = input.required<string>();

  /**
   * The single narrowing point for `articleId`. Non-numeric or empty input
   * yields `null`, which callers treat as "nothing to look up" rather than
   * querying with `NaN`.
   */
  private readonly numericArticleId = computed<number | null>(() => {
    const raw = this.articleId();
    if (raw === '' || raw === null || raw === undefined) {
      return null;
    }
    const numeric = Number(raw);
    return Number.isFinite(numeric) ? numeric : null;
  });

  private registeredToken: number | null = null;

  /**
   * Resolves `company` the same way `InventoryJobsService.getCompanyId()`
   * does internally for its own calls (`getJobs`/`getCounts`/`retryJob`).
   * `getArticleWarnings` takes `company` from the caller instead, so this
   * component — the caller — mirrors that one-liner rather than adding a
   * second company-resolution helper.
   */
  private getCompanyId(): number {
    const companyObject = this.cookieAuthService.getCompanyObject;
    return Number(companyObject?.Id_company) || 0;
  }

  /**
   * Re-queries whenever `articleId` changes. `catchError` swallows a failed
   * lookup into "no warning" rather than surfacing an error — this
   * component is a passive, non-blocking reporter, never something that
   * should surface an error state of its own.
   */
  private readonly warnings = toSignal(
    toObservable(this.numericArticleId).pipe(
      switchMap((articleId) =>
        articleId === null
          ? of(EMPTY_WARNINGS)
          : this.inventoryJobsService
              .getArticleWarnings(this.getCompanyId(), [articleId])
              .pipe(catchError(() => of(EMPTY_WARNINGS))),
      ),
    ),
    { initialValue: EMPTY_WARNINGS },
  );

  /**
   * Undefined when the article has no unresolved warning. Both sides are
   * compared as numbers because the backend's `articleId` and this input can
   * each arrive as a string depending on the driver and the call site.
   */
  private readonly warning = computed(() => {
    const articleId = this.numericArticleId();
    return articleId === null
      ? undefined
      : this.warnings().find((w) => Number(w.articleId) === articleId);
  });

  constructor() {
    // If `articleCode` changed without `articleId` also changing, the store
    // would keep the stale code against the (unchanged) registered token.
    // Not handled here: all 12 call sites currently always change both
    // together, so this is a real but unreachable edge case today.
    toObservable(this.warning)
      .pipe(takeUntilDestroyed())
      .subscribe((w) => {
        if (this.registeredToken !== null) {
          this.store.unregister(this.registeredToken);
          this.registeredToken = null;
        }

        const articleId = this.numericArticleId();
        if (w && articleId !== null) {
          this.registeredToken = this.store.register({
            articleId,
            articleCode: this.articleCode(),
            warning: w,
          });
        }
      });

    // `takeUntilDestroyed()` only stops future emissions on destroy — it
    // does not itself unregister the currently-held token, so that cleanup
    // needs its own explicit hook here.
    inject(DestroyRef).onDestroy(() => {
      if (this.registeredToken !== null) {
        this.store.unregister(this.registeredToken);
      }
    });
  }
}
